/**
 * VLX Tabs
 * Accessible tabs with mouse and keyboard support
 */

(function () {
    "use strict";

    const VLX = (window.VLX = window.VLX || {});
    let nextId = 0;

    class Tabs {
        constructor(tablist) {
            this.tablist = tablist;
            this.tabs = Array.from(tablist.querySelectorAll('[data-toggle="tab"]')).filter(
                (tab) => tab.closest('[role="tablist"], [data-tabs]') === tablist,
            );

            if (!this.tabs.length) return;

            this.tablist.setAttribute("role", "tablist");
            this.orientation = this.tablist.getAttribute("aria-orientation") || "horizontal";

            this.setup();
            this.bind();
        }

        setup() {
            let active = this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true" && !this.isDisabled(tab));

            if (!active) {
                active = this.tabs.find((tab) => tab.parentElement?.classList.contains("active") && !this.isDisabled(tab));
            }

            if (!active) {
                active = this.tabs.find((tab) => !this.isDisabled(tab));
            }

            this.tabs.forEach((tab) => {
                if (!tab.id) tab.id = `vlx-tab-${++nextId}`;

                tab.setAttribute("role", "tab");

                const panel = this.panelFor(tab);
                if (panel) {
                    panel.setAttribute("role", "tabpanel");
                    panel.setAttribute("aria-labelledby", tab.id);
                }

                this.setState(tab, tab === active);
            });

            this.activeTab = active || null;
        }

        bind() {
            this.tablist.addEventListener("click", (event) => {
                const tab = event.target.closest('[data-toggle="tab"]');
                if (!tab || !this.tablist.contains(tab)) return;

                event.preventDefault();
                this.show(tab, { focus: true });
            });

            this.tablist.addEventListener("keydown", (event) => this.onKeydown(event));
        }

        onKeydown(event) {
            const current = event.target.closest('[data-toggle="tab"]');
            if (!current || !this.tablist.contains(current)) return;

            const available = this.tabs.filter((tab) => !this.isDisabled(tab));
            const index = available.indexOf(current);
            if (index < 0) return;

            let next = null;
            const previousKey = this.orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
            const nextKey = this.orientation === "vertical" ? "ArrowDown" : "ArrowRight";

            if (event.key === previousKey) {
                next = available[(index - 1 + available.length) % available.length];
            } else if (event.key === nextKey) {
                next = available[(index + 1) % available.length];
            } else if (event.key === "Home") {
                next = available[0];
            } else if (event.key === "End") {
                next = available[available.length - 1];
            }

            if (!next) return;

            event.preventDefault();
            this.show(next, { focus: true });
        }

        show(tab, options = {}) {
            if (typeof tab === "string") {
                tab = this.tablist.querySelector(tab);
            }

            if (!tab || !this.tabs.includes(tab) || this.isDisabled(tab)) return false;

            if (tab === this.activeTab) {
                if (options.focus) tab.focus();
                return true;
            }

            const previousTab = this.activeTab;
            const panel = this.panelFor(tab);
            const previousPanel = previousTab ? this.panelFor(previousTab) : null;
            const detail = { tab, panel, relatedTab: previousTab, relatedPanel: previousPanel };

            if (!this.dispatch("show", detail, true)) return false;

            this.tabs.forEach((item) => this.setState(item, item === tab));
            this.activeTab = tab;

            if (options.focus) tab.focus();

            this.dispatch("shown", detail, false);
            return true;
        }

        setState(tab, active) {
            tab.setAttribute("aria-selected", String(active));
            tab.setAttribute("tabindex", active ? "0" : "-1");
            tab.parentElement?.classList.toggle("active", active);

            const panel = this.panelFor(tab);
            if (panel) panel.hidden = !active;
        }

        panelFor(tab) {
            const id = tab.getAttribute("aria-controls");
            return id ? document.getElementById(id) : null;
        }

        isDisabled(tab) {
            return tab.disabled || tab.getAttribute("aria-disabled") === "true";
        }

        dispatch(name, detail, cancelable) {
            return this.tablist.dispatchEvent(
                new CustomEvent(`vlx.tabs.${name}`, {
                    bubbles: true,
                    cancelable,
                    detail,
                }),
            );
        }
    }

    document.addEventListener("DOMContentLoaded", () => {
        document.querySelectorAll("[data-tabs]").forEach((tablist) => {
            if (!tablist.__vlxTabs) {
                tablist.__vlxTabs = new Tabs(tablist);
            }
        });
    });

    VLX.Tabs = Tabs;
})();
